import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AccountTree as DiagramIcon,
  Widgets as ComponentsIcon,
  Warning as RisksIcon,
  Security as MitigationsIcon,
  Assessment as ReportsIcon,
  AutoAwesome as AIIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Hub as HubIcon,
  Shield as ShieldIcon,
  BubbleChart as BubbleIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSpace } from '../contexts/SpaceContext';
import diagramsService from '../services/diagrams.service';
import { componentsService } from '../services/components.service';
import { riskApi } from '../services/risk.service';
import type { Risk } from 'shared';

const GETTING_STARTED_STORAGE_KEY = 'crmd_showGettingStarted';

export const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSpace } = useSpace();

  // Getting Started visibility state (persisted in localStorage)
  const [showGettingStarted, setShowGettingStarted] = useState(() => {
    const stored = localStorage.getItem(GETTING_STARTED_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  const handleDismissGettingStarted = () => {
    setShowGettingStarted(false);
    localStorage.setItem(GETTING_STARTED_STORAGE_KEY, 'false');
  };

  // Fetch counts for current space
  const { data: diagrams = [], isLoading: diagramsLoading } = useQuery({
    queryKey: ['diagrams', currentSpace?.id],
    queryFn: () => diagramsService.getAll(currentSpace?.id ?? ''),
    enabled: !!currentSpace?.id,
  });

  const { data: components = [], isLoading: componentsLoading } = useQuery({
    queryKey: ['components', currentSpace?.id],
    queryFn: () => componentsService.getBySpace(currentSpace?.id ?? ''),
    enabled: !!currentSpace?.id,
  });

  const { data: risks = [], isLoading: risksLoading } = useQuery<Risk[]>({
    queryKey: ['risks', currentSpace?.id],
    queryFn: () => riskApi.getAll({ spaceId: currentSpace?.id ?? '' }),
    enabled: !!currentSpace?.id,
  });

  const isLoading = diagramsLoading || componentsLoading || risksLoading;

  // Calculate risk statistics
  const highRisks = risks.filter(r => {
    const score = Number(r.residualRisk) || Number(r.inherentRisk) || 0;
    return score >= 7;
  }).length;

  const quickActions = [
    {
      title: t('nav.diagrams'),
      count: diagrams.length,
      icon: DiagramIcon,
      color: '#1976d2',
      path: '/diagrams',
      description: t('dashboard.diagramsDescription'),
    },
    {
      title: t('nav.components'),
      count: components.length,
      icon: ComponentsIcon,
      color: '#2D3B45',
      path: '/components',
      description: t('dashboard.componentsDescription'),
    },
    {
      title: t('nav.risks'),
      count: risks.length,
      countBadge: highRisks > 0 ? highRisks : undefined,
      badgeColor: 'error',
      icon: RisksIcon,
      color: '#ed6c02',
      path: '/risks',
      description: t('dashboard.risksDescription'),
    },
    {
      title: t('nav.reports'),
      icon: ReportsIcon,
      color: '#2e7d32',
      path: '/reports',
      description: t('dashboard.reportsDescription'),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {currentSpace ? currentSpace.name : t('nav.dashboard')}
        </Typography>
        {currentSpace?.description && (
          <Typography variant="body1" color="text.secondary">
            {currentSpace.description}
          </Typography>
        )}
      </Box>

      {showGettingStarted && (
        <Box>
          {/* Welcome Hero Section */}
          <Paper
            sx={{
              p: 4,
              mb: 4,
              background: 'linear-gradient(135deg, #2D3B45 0%, #1A252E 100%)',
              color: 'white',
              borderRadius: 3,
              position: 'relative',
            }}
          >
            <IconButton
              onClick={handleDismissGettingStarted}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
              aria-label={t('common.close')}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              {t('dashboard.welcome.title')}
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
              {t('dashboard.welcome.subtitle')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                icon={<ShieldIcon />}
                label={t('dashboard.welcome.feature1')}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip
                icon={<BubbleIcon />}
                label={t('dashboard.welcome.feature2')}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
              <Chip
                icon={<AIIcon />}
                label={t('dashboard.welcome.feature3')}
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            </Box>
          </Paper>

          {/* How It Works Section */}
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
            {t('dashboard.howItWorks.title')}
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Step 1: Components */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: '100%', position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: 16,
                    bgcolor: '#2D3B45',
                    color: 'white',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  1
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    mt: 1,
                  }}
                >
                  <ComponentsIcon sx={{ color: '#2D3B45', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    {t('dashboard.howItWorks.step1.title')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('dashboard.howItWorks.step1.description')}
                </Typography>
                <List dense>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step1.item1')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step1.item2')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step1.item3')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Step 2: Risks */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: '100%', position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: 16,
                    bgcolor: '#ed6c02',
                    color: 'white',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  2
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    mt: 1,
                  }}
                >
                  <RisksIcon sx={{ color: '#ed6c02', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    {t('dashboard.howItWorks.step2.title')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('dashboard.howItWorks.step2.description')}
                </Typography>
                <List dense>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step2.item1')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step2.item2')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step2.item3')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>

            {/* Step 3: Mitigations */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 3, height: '100%', position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: 16,
                    bgcolor: '#2e7d32',
                    color: 'white',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  3
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                    mt: 1,
                  }}
                >
                  <MitigationsIcon sx={{ color: '#2e7d32', fontSize: 28 }} />
                  <Typography variant="h6" fontWeight="bold">
                    {t('dashboard.howItWorks.step3.title')}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {t('dashboard.howItWorks.step3.description')}
                </Typography>
                <List dense>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step3.item1')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step3.item2')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon color="success" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('dashboard.howItWorks.step3.item3')}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>

          {/* Entity Flow Diagram */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t('dashboard.entityFlow.title')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                flexWrap: 'wrap',
                py: 2,
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    bgcolor: '#2D3B45',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  <ComponentsIcon sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('dashboard.entityFlow.component')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.entityFlow.componentDesc')}
                </Typography>
              </Box>

              <ArrowIcon sx={{ color: 'text.secondary', fontSize: 32 }} />

              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    bgcolor: '#ed6c02',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  <RisksIcon sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('dashboard.entityFlow.risk')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.entityFlow.riskDesc')}
                </Typography>
              </Box>

              <ArrowIcon sx={{ color: 'text.secondary', fontSize: 32 }} />

              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    bgcolor: '#2e7d32',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 1,
                  }}
                >
                  <MitigationsIcon sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('dashboard.entityFlow.mitigation')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('dashboard.entityFlow.mitigationDesc')}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* AI Assistant Section */}
          <Paper
            sx={{
              p: 3,
              mb: 4,
              border: '2px solid',
              borderColor: '#42B8B2',
              bgcolor: 'rgba(66, 184, 178, 0.05)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: '#42B8B2',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AIIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {t('dashboard.ai.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.ai.subtitle')}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CheckIcon
                    color="success"
                    fontSize="small"
                    sx={{ mt: 0.5 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('dashboard.ai.feature1.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.ai.feature1.desc')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CheckIcon
                    color="success"
                    fontSize="small"
                    sx={{ mt: 0.5 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('dashboard.ai.feature2.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.ai.feature2.desc')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CheckIcon
                    color="success"
                    fontSize="small"
                    sx={{ mt: 0.5 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('dashboard.ai.feature3.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.ai.feature3.desc')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CheckIcon
                    color="success"
                    fontSize="small"
                    sx={{ mt: 0.5 }}
                  />
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('dashboard.ai.feature4.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('dashboard.ai.feature4.desc')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Getting Started CTA */}
          <Alert
            severity="info"
            icon={<HubIcon />}
            sx={{
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('dashboard.getStarted.title')}
                </Typography>
                <Typography variant="body2">
                  {t('dashboard.getStarted.description')}
                </Typography>
              </Box>
            </Box>
          </Alert>
        </Box>
      )}

      {currentSpace && !showGettingStarted && (
        <>
          {/* Quick Actions Grid */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {quickActions.map(action => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={action.path}>
                <Card
                  sx={{
                    height: '100%',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(action.path)}
                    sx={{ height: '100%', p: 2 }}
                  >
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: `${action.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        <action.icon
                          sx={{ fontSize: 32, color: action.color }}
                        />
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {action.title}
                      </Typography>
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : action.count !== undefined ? (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <Typography variant="h4" color={action.color}>
                            {action.count}
                          </Typography>
                          {action.countBadge !== undefined && (
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: 'error.main',
                                color: 'white',
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                fontWeight: 'bold',
                              }}
                            >
                              {action.countBadge} {t('dashboard.highRisk')}
                            </Typography>
                          )}
                        </Box>
                      ) : null}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        {action.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Recent Activity / Summary Section */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <MitigationsIcon color="success" />
                  <Typography variant="h6">
                    {t('dashboard.securityStatus')}
                  </Typography>
                </Box>
                {isLoading ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : risks.length === 0 ? (
                  <Alert severity="info">{t('dashboard.noRisksYet')}</Alert>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t('dashboard.totalRisks')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {risks.length}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="error.main">
                        {t('risk.level.high')}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="error.main"
                      >
                        {highRisks}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="warning.main">
                        {t('risk.level.medium')}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="warning.main"
                      >
                        {
                          risks.filter(r => {
                            const score =
                              Number(r.residualRisk) ||
                              Number(r.inherentRisk) ||
                              0;
                            return score >= 5 && score < 7;
                          }).length
                        }
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography variant="body2" color="success.main">
                        {t('risk.level.low')}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="success.main"
                      >
                        {
                          risks.filter(r => {
                            const score =
                              Number(r.residualRisk) ||
                              Number(r.inherentRisk) ||
                              0;
                            return score < 5;
                          }).length
                        }
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  <DiagramIcon color="primary" />
                  <Typography variant="h6">
                    {t('dashboard.recentDiagrams')}
                  </Typography>
                </Box>
                {isLoading ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : diagrams.length === 0 ? (
                  <Alert severity="info">{t('dashboard.noDiagramsYet')}</Alert>
                ) : (
                  <Box>
                    {diagrams.slice(0, 3).map(diagram => (
                      <Box
                        key={diagram.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 1,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { borderBottom: 'none' },
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          px: 1,
                          mx: -1,
                          borderRadius: 1,
                        }}
                        onClick={() =>
                          navigate(
                            `/spaces/${currentSpace?.id}/diagrams/${diagram.id}`
                          )
                        }
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {diagram.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {diagram.diagramData.nodes.length}{' '}
                            {t('diagram.components')}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(diagram.updatedAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    ))}
                    {diagrams.length > 3 && (
                      <Typography
                        variant="body2"
                        color="primary"
                        sx={{
                          mt: 1,
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => navigate('/diagrams')}
                      >
                        {t('dashboard.viewAllDiagrams')} ({diagrams.length})
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Container>
  );
};
